import { OrderPresetSearchSelect } from '@src/components/OrderPresets/OrderPresetSearchSelect';
import { SubAccountSearchSelect } from '@src/components/SubAccounts/SubAccountSearchSelect';
import { useSubAccountSearchSelectOptions } from '@src/components/SubAccounts/useSubAccountSearchSelectOptions';
import { useOrderPresets, type IOrderPreset } from '@src/providers/OrderPresetsContext';
import { useSubAccounts } from '@src/providers/SubAccountsContext';
import { useTradingSettings } from '@src/providers/TradingSettingsContext';
import { Box, FormControlSizes, HStack, MixpanelEvent, useMixpanel, type ISubaccount } from '@talos/kyoko';

const PRICE_GRID_PRIMING_CONTROL_TOOLTIP_PROPS = {
  placement: 'top-start',
} as const;

export interface PriceGridPrimingControlsProps {
  orderPresetID?: string;
  subAccount?: string;
  onOrderPresetChange: (orderPresetID?: string) => void;
  onSubAccountChange: (subAccount?: string) => void;
}

export function PriceGridPrimingControls({
  orderPresetID,
  subAccount,
  onOrderPresetChange,
  onSubAccountChange,
}: PriceGridPrimingControlsProps) {
  const mixpanel = useMixpanel();
  const { orderPresetsByID, orderPresetsList, updateOrderPreset } = useOrderPresets();
  const { subAccountsByName } = useSubAccounts();
  const { enableDefaultMarketCardSubAccount } = useTradingSettings();
  const subAccountOptions = useSubAccountSearchSelectOptions();
  const selectedOrderPreset = orderPresetID ? orderPresetsByID.get(orderPresetID) : undefined;
  const selectedSubAccount = subAccount ? subAccountsByName.get(subAccount) : undefined;
  const showOrderPresets = orderPresetsList.length > 0;
  const showSubAccountSelector = enableDefaultMarketCardSubAccount && subAccountOptions.length > 0;

  if (!showOrderPresets && !showSubAccountSelector) {
    return null;
  }

  const handleOrderPresetChange = (orderPreset?: IOrderPreset) => {
    mixpanel.track(MixpanelEvent.SelectOrderPreset);
    onOrderPresetChange(orderPreset?.id);
  };

  const handleSubAccountChange = (subAccount?: ISubaccount) => {
    mixpanel.track(MixpanelEvent.SelectSubAccount);
    onSubAccountChange(subAccount?.Name);
  };

  const handleToggleFavoritePreset = (orderPreset: IOrderPreset) => {
    updateOrderPreset(orderPreset.id, {
      ...orderPreset,
      isFavorite: !orderPreset.isFavorite,
    });
  };

  return (
    <HStack gap="spacing8">
      {showOrderPresets ? (
        <Box w="180px">
          <OrderPresetSearchSelect
            selection={selectedOrderPreset}
            onChange={handleOrderPresetChange}
            size={FormControlSizes.Tiny}
            showNavigation
            centered
            showChevron={false}
            options={orderPresetsList}
            id="price-grid-preset-selector"
            data-testid="price-grid-preset-selector"
            onToggleFavorite={handleToggleFavoritePreset}
            tooltipProps={PRICE_GRID_PRIMING_CONTROL_TOOLTIP_PROPS}
          />
        </Box>
      ) : null}
      {showSubAccountSelector ? (
        <Box w="180px">
          <SubAccountSearchSelect
            selection={selectedSubAccount}
            size={FormControlSizes.Tiny}
            showNavigation
            centered
            showChevron={false}
            options={subAccountOptions}
            onChange={handleSubAccountChange}
            data-testid="price-grid-sub-account-selector"
            tooltipProps={PRICE_GRID_PRIMING_CONTROL_TOOLTIP_PROPS}
          />
        </Box>
      ) : null}
    </HStack>
  );
}
